import { useCallback, useMemo } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { useCartContext } from '~/components/Cart/CartDrawerContext';
import CartItem from '~/components/Cart/CartItem';
import { Button } from '~/components/ui/button';
import { SheetContent, SheetFooter, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { paths } from '~/constants/paths';
import { useGetCart } from '~/hooks/useCart';

export default function CartContent() {
  const navigate = useNavigate();
  const { data: cart } = useGetCart();
  const { setCartOpen } = useCartContext();

  const addCartIsPending = useIsMutating({ mutationKey: ['add-cart-product'] }) > 0;
  const removeCartIsPending = useIsMutating({ mutationKey: ['cart-remove-item'] }) > 0;

  const cartItems = useMemo(
    () => (cart?.items ?? []).sort((a, b) => a.product.title.localeCompare(b.product.title)),
    [cart]
  );
  const cartEmpty = useMemo(() => cartItems.length === 0, [cartItems]);

  const goToCheckout = useCallback(() => {
    setCartOpen(false);
    navigate(paths.checkoutPatientInfo);
  }, [setCartOpen, navigate]);

  return (
    <SheetContent className="w-[576px] overflow-x-hidden overflow-y-scroll p-4 sm:max-w-xl">
      <SheetHeader className="border-b border-gray-100 pb-2">
        <SheetTitle className="flex items-end gap-2">
          <div className="font-heading text-3xl font-semibold text-gray-900">
            {!cartEmpty ? 'Your bag' : 'Your bag is empty'}
          </div>
          <div className="text-sm font-light text-gray-500">
            {!cartEmpty && `${cartItems.length} items`}
          </div>
        </SheetTitle>
      </SheetHeader>

      <section className="py-6">
        {!cartEmpty ? (
          <>
            <div className="flex flex-col gap-3">
              {cartItems.map((item) => (
                <CartItem key={item.product.id} item={item} />
              ))}
            </div>

            <SheetFooter className="mt-6">
              <Button
                variant="primary"
                disabled={addCartIsPending || removeCartIsPending}
                onClick={goToCheckout}
              >
                Confirm order
              </Button>
            </SheetFooter>
          </>
        ) : (
          <p className="text-sm italic">There are no products in your bag</p>
        )}
      </section>
    </SheetContent>
  );
}
